import { BEIGE_300, CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { Link, useMediaQuery, useTheme } from '@mui/material'
import { getPrivacyPolicyLink, getTermsLink } from 'utils/localization'
import i18n, { transformLanguageToAuth0Value } from 'translations/i18n'

import { AdminDashboard } from '../Administrator/Dashboard'
import Box from '@mui/material/Box'
import { CreativeGroupedDashboard } from '../Creative/Dashboard'
import { LanguageDropdown } from 'components/common/TopBar/LanguageDropdown'
import Logo from 'components/common/Logo/Logo'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import Modal from 'components/common/Modals/Modal/Modal'
import { PageTransition } from 'utils/animations'
import { PropertiesDashboard } from '../PropertiesDashboard/_main'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { forgotPasswordPath } from '../General/ForgotPassword'
import { getLandingImage } from 'constants/home'
import { getOnboardingCTLink } from 'utils/localization/getOnboardingCTLink'
import { Path } from 'constants/router'
import { logAnalyticsEvent } from 'utils/analytics'
import { useAuth0 } from 'utils/auth'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component
 * Index is the main landing page component for the platform.
 * 
 * This component handles user authentication state and role-based redirection:
 * - Redirects authenticated users to their respective dashboards (Admin, Creative, Client).
 * - Displays a landing page for unauthenticated users, including login, sign-up, and password reset options.
 * - Shows a modal if the user is blocked, based on URL parameters.
 * - Includes links to terms, privacy policy.
 * - Uses the upbranding design.
 * 
 * @example
 * <Index />
 */
export const Index: React.FC = (props: any) => {

  const { isAuthenticated, roles, loginWithRedirect } = useAuth0()
  const navigate = useNavigate()
  const { clientData, activePersonalUserWorkspaces, activeSubscribedUserWorkspaces } = useUserData()
  const isForgotPasswordPageEnabled = useFeatureFlag(FeatureFlag.PUBLIC_FORGOT_PASSWORD_PAGE)

  const hasNoWorkspaces = !!clientData && activePersonalUserWorkspaces.length === 0 && activeSubscribedUserWorkspaces.length === 0
  const { t } = useTranslation(['index', 'authentication', 'terms_and_privacy_footer'])
  const theme = useTheme()

  const params = new URLSearchParams(window.location.search)

  const isUserBlocked = params.get('error_description') === 'user is blocked' && params.get('error') === 'unauthorized'
  const isClientLoginPage = params.has('login_client')
  const [isUserBlockedModalOpen, setIsUserBlockedModalOpen] = useState(isUserBlocked)

  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'))
  const isMediumView = useMediaQuery(theme.breakpoints.down('md'))
  const isLargeDesktopView = useMediaQuery(theme.breakpoints.up('xl'))
  const isLandingImageHidden = useMediaQuery('@media (max-width:1024px)') // Custom breakpoint for 1024px and smaller screens

  useEffect(() => {
    if (isAuthenticated && roles.isClient && hasNoWorkspaces) {
      navigate(Path.PROFILE_SETTINGS, { replace: true })
    }
  }, [isAuthenticated, roles.isClient, hasNoWorkspaces, navigate])

  if (isAuthenticated && (roles.isAdmin)) return <AdminDashboard {...props} />

  if (isAuthenticated && (roles.isCreative)) return <CreativeGroupedDashboard />

  if (isAuthenticated && roles.isClient) {
    if (hasNoWorkspaces) return null
    return <PropertiesDashboard {...props} />
  }

  return (
    <PageTransition>
      <Stack
        minWidth="30vh"
        height="100vh"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        bgcolor={BEIGE_300}
      >

        {/** MAIN CONTENT */}
        <Stack
          flex={1}
          gap="1rem"
          height="100%"
          textAlign="center"
          position="relative"
          alignItems="center"
          justifyContent="center"
          width={isMediumView ? '100%' : isLargeDesktopView ? '65vw' : '50vw'}
          paddingX={!isMobileView ? '0' : '2rem'}
        >

          {/** Language dropwdown */}
          <Stack position="absolute" top="1rem" right="1rem" zIndex={1}>
            <LanguageDropdown />
          </Stack>

          {/** Logo */}
          <Stack width="100%" position="absolute" alignItems="center" top={isLargeDesktopView ? '20%' : '15%'}>
            <Logo width="10rem" height="1.2rem" />
          </Stack>

          <Stack lineHeight={2} alignItems="center">
            <Typography variant="display-md">{t('heading1')}</Typography>
            <Typography variant="display-md">{t('heading2')}</Typography>
          </Stack>

          <Typography variant="text-sm" color={GRAY_700}>
            {t('text1')}{' '}{t('text2')}
          </Typography>

          <Box width={!isMobileView ? '36rem' : '30rem'} marginTop="2rem">

            <MUIButton
              fullWidth
              onClick={() => {
                logAnalyticsEvent('clicks_login', { location: 'Index' })
                loginWithRedirect({
                  appState: { targetUrl: window.location.pathname },
                  authorizationParams: {
                    mode: 'login',
                    language: transformLanguageToAuth0Value(i18n.language),
                    login_hint: params.has('userEmail') ? params.get('userEmail') ?? undefined : undefined,
                  },
                })
              }}
            >
              <Typography variant="text-md" fontWeight={450}>
                {t('authentication:log_in')}
              </Typography>
            </MUIButton>

            <MUIDivider>
              <Typography variant="text-xs" color={GRAY_700}>{t('or')}</Typography>
            </MUIDivider>

            {/** Sign up buttons */}
            <Stack flexDirection="row" justifyContent="space-between" gap="1rem">
              <MUIButton
                fullWidth
                type="secondaryBorder"
                onClick={() => {
                  logAnalyticsEvent('clicks_sign_up', { location: 'Index' })
                  loginWithRedirect({
                    appState: { targetUrl: window.location.pathname },
                    authorizationParams: {
                      mode: 'signUp',
                      language: transformLanguageToAuth0Value(i18n.language),
                      login_hint: params.has('userEmail') ? params.get('userEmail') ?? undefined : undefined,
                    },
                  })
                }}
              >
                {/* On the Client page (with 'login_client' in the URL parameters), we don't distinguish between Clients and Creatives. */}
                <Typography variant="text-md" fontWeight={450}>
                  {t(isClientLoginPage ? 'authentication:sign_up' : 'authentication:client_sign_up')}
                </Typography>
              </MUIButton>

              {!isClientLoginPage &&
                <MUIButton
                  fullWidth
                  type="secondaryBorder"
                  onClick={() => {
                    logAnalyticsEvent('clicks_creative_sign_up', { location: 'Index' })
                    window.open(getOnboardingCTLink(i18n.language), '_self', 'noopener noreferrer')
                  }}
                >
                  <Typography variant="text-md" fontWeight={450}>
                    {t('authentication:creative_sign_up')}
                  </Typography>
                </MUIButton>
              }
            </Stack>

            {/** Lost password */}
            <Stack gap="0.5rem" direction="row" marginTop="2.4rem" justifyContent="center" color={GRAY_700}>
              <Typography variant="text-xs">
                {t('authentication:lost_password')}
              </Typography>

              <Link
                variant="text-xs"
                href="#lostpassword"
                color={GRAY_900}
                underline="none"
                onClick={e => {
                  e.preventDefault()
                  logAnalyticsEvent('clicks_lost_password', { location: 'Index' })
                  if (isForgotPasswordPageEnabled) {
                    navigate(forgotPasswordPath(params.get('userEmail')))
                    return
                  }
                  loginWithRedirect({
                    appState: { targetUrl: window.location.pathname },
                    authorizationParams: {
                      mode: 'forgotPassword',
                      language: transformLanguageToAuth0Value(i18n.language),
                      login_hint: params.has('userEmail') ? params.get('userEmail') ?? undefined : undefined,
                    },
                  })
                }}
              >
                {t('authentication:reset_now')}
              </Link>
            </Stack>

          </Box>

          {/** Links for terms and conditions and privacy policy */}
          <Stack width="100%" position="absolute" bottom="2rem" paddingX={isMobileView ? '2rem' : '4rem'}>
            <Typography variant="text-xs" color={GRAY_700}>
              {`${t('terms_and_privacy_footer:terms_agreement')} `}
              <Link variant="text-xs" color={GRAY_900} underline="none" href={getTermsLink(i18n.language)}>
                {t('terms_and_privacy_footer:terms_and_conditions')}
              </Link>
              {` ${t('terms_and_privacy_footer:and')} `}
              <Link variant="text-xs" color={GRAY_900} underline="none" href={getPrivacyPolicyLink(i18n.language)}>
                {t('terms_and_privacy_footer:privacy_policy')}
              </Link>
            </Typography>
          </Stack>
        </Stack>

        {/** RIGHT CONTENT (background image) */}
        {!isLandingImageHidden &&
          <Box
            sx={{
              height: '100%',
              width: '50vw',
              backgroundSize: 'cover',
              backgroundPosition: 'right',
              backgroundRepeat: 'no-repeat',
              backgroundImage: `url(${getLandingImage(i18n.language)})`,
              aspectRatio: !isMediumView ? '0.7 / 1' : isLargeDesktopView ? '0.6 / 1' : '1 / 1',
            }}
          >
            <Stack alignItems="center" justifyContent="flex-end" height="30%" paddingX="2rem" textAlign="center">
              <Typography variant={isLargeDesktopView ? 'display-lg' : 'display-md'} color={BEIGE_300}>
                {t('step_into_new_era')}
              </Typography>

              <Typography variant={isLargeDesktopView ? 'display-lg' : 'display-md'} color={CORAL_600}>
                {t('real_estate_marketing')}
              </Typography>
            </Stack>
          </Box>
        }

        {/** Blocked user modal */}
        <Modal
          className="blocked-user-popup"
          isOpen={isUserBlockedModalOpen}
          modalContentClassName="blocked-user-popup-modal-content"
        >
          <Stack gap="2rem" alignItems="flex-start">

            <Typography variant="h3">
              {t('authentication:blocked_user')}
            </Typography>

            <MUIButton onClick={() => setIsUserBlockedModalOpen(false)}>
              <Typography variant="text-md" fontWeight={450}>
                {t('authentication:blocked_user_confirmed').toUpperCase()}
              </Typography>
            </MUIButton>

          </Stack>
        </Modal>

      </Stack>
    </PageTransition>
  )
}
